/** Highest → lowest lineage order for membership section placement. */
export const WORKPANEL_LINEAGE_ORDER = [
    "workspace-group",
    "workspace",
    "board-group",
    "board",
    "item-group",
    "item",
];

export const WORKPANEL_LEVEL_LABELS = {
    item: "Item",
    "item-group": "Item Group",
    board: "Board",
    "board-group": "Board Group",
    workspace: "Space",
    "workspace-group": "Space Group",
};

/**
 * @param {string|null|undefined} roleTitle
 * @returns {number}
 */
export function getWorkPanelRolePrivilegeRank(roleTitle) {
    switch (roleTitle) {
        case "owner":
            return 3;
        case "admin":
            return 2;
        case "member":
            return 1;
        default:
            return 0;
    }
}

/**
 * Highest lineage among role model_types (Space Group → Item).
 *
 * @param {Array<{model_type?: string}>|null|undefined} roles
 * @param {Record<string, string>|string[]} accessLevels
 * @returns {string}
 */
export function getHighestLineageAccessLevel(roles, accessLevels) {
    const levels = Array.isArray(accessLevels)
        ? accessLevels
        : Object.values(accessLevels ?? {});

    let highest_level = "";
    let highest_index = Number.NEGATIVE_INFINITY;

    for (const role of roles ?? []) {
        const access_level = role?.model_type;
        const access_level_index = levels.findIndex(
            (level) => level === access_level,
        );

        if (access_level_index > highest_index) {
            highest_index = access_level_index;
            highest_level = access_level;
        }
    }

    return highest_level;
}

/**
 * Roles above the current resource in the work-panel tree.
 *
 * @param {Array<{model_type?: string}>|null|undefined} roles
 * @param {string|null|undefined} currentLevel
 * @returns {Array<{title?: string, model_type?: string}>}
 */
export function getUplineRolesForLevel(roles, currentLevel) {
    const current_index = WORKPANEL_LINEAGE_ORDER.indexOf(currentLevel);

    if (current_index < 0) {
        return [...(roles ?? [])];
    }

    return (roles ?? []).filter((role) => {
        const role_index = WORKPANEL_LINEAGE_ORDER.indexOf(role?.model_type);

        return role_index >= 0 && role_index < current_index;
    });
}

/**
 * @param {Array<{title?: string, model_type?: string}>|null|undefined} roles
 * @param {string|null|undefined} currentLevel
 * @returns {{title: string, model_type: string}|null}
 */
export function getOwnRoleAtLevel(roles, currentLevel) {
    const own_role = (roles ?? []).find(
        (role) => role?.model_type === currentLevel,
    );

    if (!own_role) {
        return null;
    }

    return {
        title: own_role.title ?? own_role.role,
        model_type: own_role.model_type ?? currentLevel,
    };
}

/**
 * @param {Array<{title?: string, model_type?: string}>|null|undefined} roles
 * @returns {{title: string, model_type: string}|null}
 */
export function getHighestPrivilegeRoleEntry(roles) {
    let best = null;
    let best_rank = 0;
    let best_specificity = Number.POSITIVE_INFINITY;

    for (const role of roles ?? []) {
        const title = role?.title ?? role?.role;
        const rank = getWorkPanelRolePrivilegeRank(title);

        if (rank <= 0) {
            continue;
        }

        const specificity = WORKPANEL_LINEAGE_ORDER.indexOf(role?.model_type);

        if (
            rank > best_rank ||
            (rank === best_rank &&
                specificity >= 0 &&
                specificity < best_specificity)
        ) {
            best = {
                title,
                model_type: role?.model_type ?? "",
            };
            best_rank = rank;
            best_specificity =
                specificity >= 0 ? specificity : Number.POSITIVE_INFINITY;
        }
    }

    return best;
}

/**
 * Whether a profile may edit membership (invite / All-tab select / role changes).
 * Uses highest privilege across roles[] — not lineage-primary `role`
 * (e.g. space member + board admin is editable).
 *
 * @param {object|null|undefined} profile
 * @param {{ OWNER?: string, ADMIN?: string }|null|undefined} [rolesEnum]
 * @returns {boolean}
 */
export function canEditWorkPanelMembershipFromProfile(profile, rolesEnum = {}) {
    if (!profile) {
        return false;
    }

    const owner_role = rolesEnum.OWNER ?? "owner";
    const admin_role = rolesEnum.ADMIN ?? "admin";
    const editable_roles = [owner_role, admin_role];
    const highest = getHighestPrivilegeRoleEntry(profile.roles);
    const privilege = highest?.title ?? profile.role;

    return editable_roles.includes(privilege);
}

/**
 * Level-prefixed labels (e.g. "Board Admin") only when the user has multiple roles.
 *
 * @param {string|null|undefined} modelType
 * @param {string|null|undefined} roleTitle
 * @param {{ useLevelPrefix?: boolean }} [options]
 * @returns {string}
 */
export function formatWorkPanelRoleLabel(
    modelType,
    roleTitle,
    { useLevelPrefix = false } = {},
) {
    if (roleTitle === "member") {
        return "Member";
    }

    if (roleTitle === "owner") {
        return "Owner";
    }

    if (roleTitle === "admin") {
        if (useLevelPrefix) {
            const level_label = WORKPANEL_LEVEL_LABELS[modelType];

            return level_label ? `${level_label} Admin` : "Admin";
        }

        return "Admin";
    }

    return roleTitle ?? "Member";
}

/**
 * True when strongest upline role is member — user can elevate to (or demote from)
 * admin at the current resource, including after an elevation already exists.
 *
 * @param {object|null|undefined} member
 * @param {object} invitationStore
 * @param {string|null|undefined} currentLevel
 * @returns {boolean}
 */
export function canElevateHigherLevelMember(
    member,
    invitationStore,
    currentLevel = null,
) {
    if (!invitationStore.user_can_edit) {
        return false;
    }

    if (
        member?.role === invitationStore.ROLES.OWNER ||
        member?.support_admin
    ) {
        return false;
    }

    const roles = member?.roles ?? [];
    const upline_roles = currentLevel
        ? getUplineRolesForLevel(roles, currentLevel)
        : roles;
    const strongest_upline = getHighestPrivilegeRoleEntry(
        upline_roles.length ? upline_roles : roles,
    );
    const parent_role = strongest_upline?.title ?? member?.role;

    if (parent_role !== invitationStore.ROLES.MEMBER) {
        return false;
    }

    // Parent-only, or already elevated at current level under a member upline.
    if (member?.has_parent_access) {
        return true;
    }

    const own_role = getOwnRoleAtLevel(roles, currentLevel);

    return (
        own_role?.title === invitationStore.ROLES.ADMIN ||
        member?.role === invitationStore.ROLES.ADMIN
    );
}

/**
 * @param {object|null|undefined} member
 * @param {object} invitationStore
 * @param {string|null|undefined} currentLevel
 * @returns {boolean}
 */
export function isHigherLevelRoleSelectDisabled(
    member,
    invitationStore,
    currentLevel = null,
) {
    if (!invitationStore.user_can_edit) {
        return true;
    }

    if (
        canElevateHigherLevelMember(member, invitationStore, currentLevel)
    ) {
        return false;
    }

    if (!member?.has_parent_access) {
        return false;
    }

    return true;
}

/**
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object|null|undefined} params.member
 * @param {string|null|undefined} params.currentLevel
 * @returns {Array<{label: string, value: string, disabled?: boolean}>}
 */
export function buildWorkPanelMemberRoleOptions({
    invitationStore,
    member,
    currentLevel,
}) {
    if (
        canElevateHigherLevelMember(member, invitationStore, currentLevel)
    ) {
        const level_label =
            WORKPANEL_LEVEL_LABELS[currentLevel] ??
            WORKPANEL_LEVEL_LABELS.item;

        return [
            {
                label: "Member",
                value: invitationStore.ROLES.MEMBER,
            },
            {
                label: `${level_label}\u00A0Admin`,
                value: invitationStore.ROLES.ADMIN,
            },
        ];
    }

    return [
        {
            label: "Admin",
            value: invitationStore.ROLES.ADMIN,
        },
        {
            label: "Member",
            value: invitationStore.ROLES.MEMBER,
        },
    ];
}

/**
 * Resolve the selected dropdown option, including elevated display labels.
 * Prefers highest-privilege role across roles[] over API primary `role`
 * (which is highest-lineage, not highest privilege).
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object|null|undefined} params.member
 * @param {string|null|undefined} params.currentLevel
 * @param {Array<{label: string, value: string}>} params.roleOptions
 * @returns {{label: string, value: string}}
 */
export function resolveSelectedWorkPanelRoleOption({
    invitationStore,
    member,
    currentLevel,
    roleOptions,
}) {
    const roles = member?.roles ?? [];
    const own_role = getOwnRoleAtLevel(roles, currentLevel);
    const can_elevate = canElevateHigherLevelMember(
        member,
        invitationStore,
        currentLevel,
    );
    const level_label =
        WORKPANEL_LEVEL_LABELS[currentLevel] ?? WORKPANEL_LEVEL_LABELS.item;
    // "Board Admin" / "Space Group Admin" only when multiple lineage roles exist.
    const use_level_prefix = roles.length > 1;

    // Active elevation UI at the current resource (Member / {Level} Admin).
    if (can_elevate) {
        const elevated_at_current =
            own_role?.title === invitationStore.ROLES.ADMIN ||
            (!own_role && member?.role === invitationStore.ROLES.ADMIN);

        if (elevated_at_current) {
            return {
                label: use_level_prefix
                    ? `${level_label}\u00A0Admin`
                    : "Admin",
                value: invitationStore.ROLES.ADMIN,
            };
        }

        return {
            label: "Member",
            value: invitationStore.ROLES.MEMBER,
        };
    }

    // Direct role on the current resource.
    if (own_role) {
        return {
            label: formatWorkPanelRoleLabel(own_role.model_type, own_role.title, {
                useLevelPrefix: use_level_prefix,
            }),
            value: own_role.title,
        };
    }

    // Parent-only / multi-lineage: show highest privilege; prefix only if multi-role.
    const highest = getHighestPrivilegeRoleEntry(roles);

    if (highest) {
        return {
            label: formatWorkPanelRoleLabel(highest.model_type, highest.title, {
                useLevelPrefix: use_level_prefix,
            }),
            value: highest.title,
        };
    }

    const role_value = member?.role ?? invitationStore.ROLES.MEMBER;
    const matched = roleOptions?.find((option) => option.value === role_value);

    if (matched) {
        return matched;
    }

    return (
        roleOptions?.find(
            (option) => option.value === invitationStore.ROLES.MEMBER,
        ) ?? {
            label: "Member",
            value: invitationStore.ROLES.MEMBER,
        }
    );
}

/**
 * Keep local roles[] / has_parent_access in sync when elevating or demoting.
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {number|string} params.profileId
 * @param {string} params.role
 * @param {string|null|undefined} params.currentLevel
 */
export function syncLocalElevationRolesAfterChange({
    invitationStore,
    profileId,
    role,
    currentLevel,
}) {
    const profile = invitationStore.user_profiles_with_roles?.find(
        (user_profile) => Number(user_profile?.id) === Number(profileId),
    );

    if (!profile || !currentLevel) {
        return;
    }

    const roles = [...(profile.roles ?? [])];
    const own_index = roles.findIndex(
        (entry) => entry?.model_type === currentLevel,
    );

    if (role === invitationStore.ROLES.ADMIN) {
        if (own_index >= 0) {
            roles[own_index] = {
                ...roles[own_index],
                title: invitationStore.ROLES.ADMIN,
            };
        } else {
            roles.push({
                model_type: currentLevel,
                model_id: null,
                title: invitationStore.ROLES.ADMIN,
            });
        }

        profile.roles = roles;
        profile.has_parent_access = false;
        profile.role = invitationStore.ROLES.ADMIN;

        return;
    }

    if (role === invitationStore.ROLES.MEMBER) {
        const upline_roles = getUplineRolesForLevel(roles, currentLevel);
        const has_parent_membership = upline_roles.length > 0;

        if (has_parent_membership) {
            // Undo elevation: fall back to inherited parent membership.
            profile.roles = roles.filter(
                (entry) => entry?.model_type !== currentLevel,
            );
            profile.has_parent_access = true;
        } else {
            // Direct demotion: keep a local member role at this level.
            if (own_index >= 0) {
                roles[own_index] = {
                    ...roles[own_index],
                    title: invitationStore.ROLES.MEMBER,
                };
            } else {
                roles.push({
                    model_type: currentLevel,
                    model_id: null,
                    title: invitationStore.ROLES.MEMBER,
                });
            }

            profile.roles = roles;
            profile.has_parent_access = false;
        }

        profile.role = invitationStore.ROLES.MEMBER;
    }
}
